import {Time} from '../interfaces/time';
import {SimpleTime} from '../models/simple-time';
/**
 * If you don't need to extend i18n to suit your own needs then just do: import i18n from 'common-utils'; to get a singleton object.
 * If you need to extend it import the class and then extend it: import {I18nUtil} from 'common-utils';
 * If you choose to import the class it will be up to the consuming application to maintain a singleton object.
 */

let defaultLocaleObject = {
  'LOCALE': 'en_US',
  'DATETIME_FORMATS': {
    'MONTH': ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    'SHORTMONTH': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    'medium': 'MM/dd/yyyy hh:mm:ss a',
    'mediumDateText': 'MM/DD/YYYY',
    'shortDateText': 'MM/DD/YY',
    'AMPMS': ['AM', 'PM'],
    'mediumDate': 'MM/dd/yyyy',
    'mediumTime': 'hh:mm:ss a',
    'shortTime': 'hh:mm a',
    'short': 'M/d/yy hh:mm a',
    'SHORTDAY': ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
    'DAY': ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    'shortDate': 'M/d/yy'
  },
  '$BREACH_MITIGATION': 'XRFmEENA55t627cN',
  'NUMBER_FORMATS': {
    'DECIMAL_SEP': '.',
    'number_regex': '([-]?(([0-9]{1,3}(,[0-9]{3})*(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))',
    'CURRENCY_SYM': '$',
    'GROUP_SEP': ',',
    'PATTERNS': [{
      'negSuf': '',
      'posSuf': '',
      'lgSize': '3',
      'minFrac': 0,
      'minInt': 1,
      'negPre': '-',
      'gSize': 3,
      'maxFrac': 9,
      'macFrac': '0',
      'posPre': ''
    }],
    'number_format': '#,###.#########'
  }
};

const PIPE_REGEX = /\|([^|]+$)/;
const AM_PM_TIME_REGEX = /^ *(0?[1-9]|10|11|12) *: *([0-5]\d) *(am|pm|AM|PM) *$/;
const TWENTY_FOUR_HOUR_TIME_REGEX = /^ *(0?[0-9]|1[0-9]|2[0-3]) *: *([0-5]\d) *$/;

export class I18nUtil {
  public twoDigitRegex: RegExp = /^\d{2}$/;
  // public timeRegexString: string = '^(0?[1-9]|10|11|12) *: *([0-5]\\d) *(am|pm)$'; //Commenting out on 11/13/2017. Delete in 2018 if no one is complaining.
  public messageKeys: Object = {
    'cu|validation engine|The number must be greater than or equal to xx': 'The number must be greater than or equal to {{minValue}}',
    'cu|validation engine|The number must be less than or equal to xx': 'The number must be less than or equal to {{maxValue}}',
    'cu|validation engine|The number must be less than xx': 'The number must be less than {{maxValue}}',
    'cu|validation engine|The number must be greater than xx': 'The number must be greater than {{minValue}}',
    'cu|validation engine|xx is not a valid integer.': '{{input}} is not a valid integer.',
    'cu|validation engine|The number cannot have exceed xx decimal places.': 'The number cannot exceed {{maxDecimals}} decimal places.',
    'cu|validation engine|The number must have xx or more digits.': 'The number must have {{minLength}} or more digits.',
    'cu|validation engine|The number must have xx or less digits.': 'The number must have {{maxLength}} or less digits.',
    'cu|validation engine|The date must be on or after xx': 'The date must be on or after {{minDate}}',
    'cu|validation engine|The date must be on or before xx': 'The date must be on or before {{maxDate}}',
    'cu|validation engine|The text must have xx or more characters.': 'The text must have {{minLength}} or more characters.',
    'cu|validation engine|The text must have xx or less characters.': 'The text must have {{maxLength}} or less characters.',
    'cu|validation engine|Must not contain any characters in xx': 'Must not contain any characters in {{excludeChars}}',
    'cu|validation engine|The time must be on or before xx': 'The time must be on or before {{maxTime}}',
    'cu|validation engine|The time must be on or after xx': 'The time must be on or after {{minTime}}',
    'cu|validation engine|The email is not in the correct format.': 'The email is not in the correct format.',
    'global|Today': 'Today',
    'global|Tomorrow': 'Tomorrow',
    'global|Yesterday': 'Yesterday',
    'cu|date utils|xx at xx': '{{theDate}} at {{theTime}}'
  };

  public locale: any = defaultLocaleObject;
  private getTextOverwriteFunction;

  overWriteGetText(getTextOverwriteFunction): void {
    this.getTextOverwriteFunction = getTextOverwriteFunction;
  }

  getShortFormat(): string {
    return this.locale.DATETIME_FORMATS.shortDate;
  }

  getDateFormat(): string {
    return this.locale.DATETIME_FORMATS.mediumDate;
  }

  getDateText(): string {
    return this.locale.DATETIME_FORMATS.mediumDateText;
  }

  /**
   * This function is expceded to be overridden by applications using Angular.
   * @param key, this should be a string unless getTextOverwriteFunction is not null.
   * @param params, this should be a string or map of strings unless getTextOverwriteFunction is not null.
   * @returns {any}, this will be a string unless getTextOverwriteFunction is not null.
   */
  getText(key?: any, params?: {[key: string]: any} | string | number | boolean): any {
    //This is for Angular2 to use ng-translate instead of the default function.
    if (this.getTextOverwriteFunction != null) {
      return this.getTextOverwriteFunction(key, params);
    }

    if (typeof key !== 'string') {
      return;
    }

    let msg = this.messageKeys[key];
    if (msg == null) {
      msg = this.stripContextFromKey(key);
    }

    if (msg == null) {
      msg = key;
    }

    if (params != null) {
      if (typeof params === 'string' ||
       typeof params === 'boolean' ||
       typeof params === 'number') {
        msg = msg.replace(/{{[^}]+}}/g, String(params));
      } else {
        for (let k of Object.keys(params)) {
          let regex = new RegExp('\\{\\{' + k + '}}', 'g');
          msg = msg.replace(regex, params[k]);
        }
      }
    }

    return msg;
  }

  stripContextFromKey(key: string): string | undefined {
    let result: RegExpExecArray | null = PIPE_REGEX.exec(key);
    if (result != null && result.length > 1) {
      return result[1];
    }

    return key;
  }

  getNow(): Date {
    //TODO: We may need to figure out how to get this to return the "now" of the server.
    return new Date();
  }

  /**
   * @param date: The date object to format
   * @param useMonthAbbeviations: Set to false for a date the screen readers can read nicely.
   * @returns String: The formatted date string.
   */
  localizeDatePretty(date: Date, useMonthAbbeviations = true): string {
    if (!(date instanceof Date)) {
      return '';
    }
    let now: Date = this.getNow();
    let tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    let yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

    if (now.getDate() === date.getDate() &&
        now.getMonth() === date.getMonth() &&
        now.getFullYear() === date.getFullYear()) {
      return this.getText('global|Today');
    } else if (tomorrow.getDate() === date.getDate() &&
               tomorrow.getMonth() === date.getMonth() &&
               tomorrow.getFullYear() === date.getFullYear()) {
      return this.getText('global|Tomorrow');
    } else if (yesterday.getDate() === date.getDate() &&
               yesterday.getMonth() === date.getMonth() &&
               yesterday.getFullYear() === date.getFullYear()) {
      return this.getText('global|Yesterday');
    }

    let dateString: string;
    let dateFormat: string = this.locale.DATETIME_FORMATS.mediumDate.toLowerCase();
    let startType: string = dateFormat.charAt(0); //'y' | 'm' | 'd'
    let endType: string = dateFormat.charAt(dateFormat.length - 1);
    let useYear: boolean = now.getFullYear() !== date.getFullYear();

    let month: string;
    let day: number = date.getDate();
    let year: number = date.getFullYear();
    if (useMonthAbbeviations) {
      month = this.locale.DATETIME_FORMATS.SHORTMONTH[date.getMonth()];
    } else {
      month = this.locale.DATETIME_FORMATS.MONTH[date.getMonth()];
    }

    switch (startType) {
      case 'd':
        if (endType === 'y') {
          //d m y
          dateString = day + ' ' + month;
          if (useYear) {
            dateString += ' ' + year;
          }
        } else {
          //d, y m
          if (useYear) {
            dateString = day + ', ' + year + ' ' + month;
          } else {
            dateString = day + ' ' + month;
          }
        }
      break;
      default:
      case 'm':
        if (endType === 'y') {
          //m d, y
          dateString = month + ' ' + day;
          if (useYear) {
            dateString += ', ' + year;
          }
        } else {
          //m y, d
          if (useYear) {
            dateString = month + ' ' + year + ', ' + day;
          } else {
            dateString = month + ' ' + day;
          }
        }
      break;
      case 'y':
        if (useYear) {
          if (endType === 'm') {
            //y, d m
            dateString = year + ', ' + day + ' ' + month;
          } else {
            //y m d
            dateString = year + ' ' + month + ' ' + day;
          }
        } else {
          if (endType === 'm') {
            //d m
            dateString = day + ' ' + month;
          } else {
            //m d
            dateString = month + ' ' + day;
          }
        }
      break;
    }
    return dateString;
  }

  localizeDate(d: Date, fmt?: 'mmdd' | undefined): string {
    if (d == null) {
      return '';
    }

    if (!this.isValidDateObject(d)) {
      return String(d);
    }

    let seperator = this.locale.DATETIME_FORMATS.mediumDate.match(/[^A-Za-z]/);
    let patternPieces = this.locale.DATETIME_FORMATS.mediumDate.split(seperator[0]);
    let localizedStrings: Array<number> = [];

    for (let i = 0; i < 3; i++) {
      switch (patternPieces[i]) {
        case 'd':
          localizedStrings.push(d.getDate());
          break;
        case 'dd':
          localizedStrings.push(d.getDate());
          break;
        case 'M':
          localizedStrings.push(d.getMonth() + 1);
          break;
        case 'MM':
          localizedStrings.push(d.getMonth() + 1);
          break;
        case 'yy':
          if (fmt !== 'mmdd') {
            localizedStrings.push(d.getFullYear());
          }
          break;
        case 'yyyy':
          if (fmt !== 'mmdd') {
            localizedStrings.push(d.getFullYear());
          }
          break;
        default:
          console.error('Unexpected format while localizing date');
      }
    }
    return localizedStrings.join(seperator);
  }

  deLocalizeDate(localizedDateString: string): Date | undefined {
    if (localizedDateString == null || localizedDateString === '') {
      return;
    }
    let seperator = this.locale.DATETIME_FORMATS.mediumDate.match(/[^A-Za-z]/);
    let patternPieces = this.locale.DATETIME_FORMATS.mediumDate.split(seperator[0]);
    let localizedPieces = localizedDateString.split(seperator);
    if (localizedPieces.length === 3) {
      let day;
      let month;
      let year;
      for (let i = 0; i < 3; i++) {
        switch (patternPieces[i]) {
          case 'd':
          case 'dd':
            day = localizedPieces[i];
            break;
          case 'M':
          case 'MM':
            month = localizedPieces[i];
            break;
          case 'yy':
          case 'yyyy':
            year = localizedPieces[i];
            if (this.twoDigitRegex.test(year)) {
              if (parseInt(year) > 50) {
                year = '19' + year;
              } else {
                year = '20' + year;
              }
            }
            break;
          default:
            console.error('Unexpected format when delocalizing date.');
        }
      }
      //  /.-,
      let d = new Date(year, month - 1, day, 0, 0, 0);
      if (isNaN(year) || isNaN(month) || isNaN(day) ||
       d.getFullYear() !== Number(year) ||
       d.getMonth() !== Number(month - 1) ||
       d.getDate() !== Number(day)) {
        return;
      }
      if (this.isValidDateObject(d)) {
        return d;
      }
    }
  }

  /**
   * @param d - a javascript date object
   */
  isValidDateObject(d: Date| undefined): boolean {
    if (d instanceof Date) {
      return !isNaN(d.getTime()); //invalid date object will return NaN for getTime
    }
    return false;
  }

  /**
   * Is the localized date string a valid date?
   * TODO - Should passing in '' evaluate to a valid date?  Maybe not so much.
   * @param localizedDate
   * @returns {any}
   */
  isValidDate(localizedDate: string): boolean {
    if (localizedDate !== '') {
      let parsedDate: Date | undefined = this.deLocalizeDate(localizedDate);
      return this.isValidDateObject(parsedDate);
    }
    return true;
  }

  /**
   * @param psJsonDateString - String - in the form of yyyy-mm-dd or yyyy-mm-dd hh:mm:ss
   * @param preserceTimeOfDay - boolean - optional param to retain hh:mm:ss in returned date
   * object. Default is false if not passed.
   * @returns {*}
   */
  parseJsonDate(psJsonDateString: string, preserveTimeOfDay?: boolean): Date | undefined {
    if (psJsonDateString == null) {
      return;
    }

    let year = 0;
    let month = 0;
    let date = 0;
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    let split = psJsonDateString.split('-');
    if (split.length < 3) {
      return;
    }
    //a valid jsonDateString may contain a time of date hour/minute/seccond
    let dayTimeOfDay = split[2].split(' ');
    if (dayTimeOfDay.length === 2) {
      //time of day is present
      split[2] = dayTimeOfDay[0];
      if (preserveTimeOfDay) {
        let timeOfDay = dayTimeOfDay[1].split(':');
        if (timeOfDay.length === 3) {
          hours = parseInt(timeOfDay[0]);
          minutes = parseInt(timeOfDay[1]);
          seconds = parseInt(timeOfDay[2]);
        }
        //else invalid hh:mm:ss - ignore it
      }
    }
    year = parseInt(split[0]);
    month = parseInt(split[1]) - 1;
    date = parseInt(split[2]);
    let returnDate = new Date(year, month, date, hours, minutes, seconds);
    if (isNaN(returnDate.getTime())) {
      return;
    }
    return returnDate;
  }

  /**
   * Sister function of parseJsonDate.  Converts a date object into the JsonDateString format
   * necessary for XTE.
   */
  getJsonDate(javascriptDateObject: Date, preserveTimeOfDay?: boolean): string | undefined {
    if (javascriptDateObject == null) {
      return;
    }

    if (isNaN(javascriptDateObject.getTime())) {
      return;
    }

    let psJsonDateString = javascriptDateObject.getFullYear() + '-' +
     this.padZeros((javascriptDateObject.getMonth() + 1), 2) + '-' +
     this.padZeros(javascriptDateObject.getDate(), 2);

    if (preserveTimeOfDay) {
      psJsonDateString += ' ' + this.padZeros(javascriptDateObject.getHours(), 2) + ':' +
       this.padZeros(javascriptDateObject.getMinutes(), 2) + ':' +
       this.padZeros(javascriptDateObject.getSeconds(), 2);
    }

    return psJsonDateString;
  }

  /**
   * Converts a JSON date to a non localized date
   * @param psJsonDateString - String - in the form of yyyy-mm-dd
   * @returns - String in the form of MM/DD/YYYY
   */
  parseJsonDateToNonLocalized(psJsonDateString: string): string {
    let date = this.parseJsonDate(psJsonDateString);
    if (date != null) {
      return this.getMmDdYyyyDateString(date);
    }
    return psJsonDateString;
  }

  /**
   * Gets the date in the form MM/DD/YYYY
   * @param date The date object to convert.
   */
  getMmDdYyyyDateString(date: Date): string {
    return (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
  }

  addDaysToDate(date, numDays): Date {
    date.setDate(date.getDate() + numDays);
    return date;
  }

  getLocalizedDayOfWeek(date): string {
    return this.locale.DATETIME_FORMATS.DAY[date.getDay()];
  }

  getNumericFormat(): { // TODO MAKE INTERFACE FOR THIS
    format: string,
    size: string,
    gs: string,
    ds: string,
    regex: string
  } {
    //{ format: '#,##0.#########', size: '3', gs: ',', ds: '.', regex: '([-]?(([0-9]{1,3}(,[0-9]{3})*(\.[0-9]+)?|\.[0-9]+)|([0-9]*(\.[0-9]+)?)))' }
    return {
      format: this.locale.NUMBER_FORMATS.number_format,
      size: this.locale.NUMBER_FORMATS.PATTERNS[0].gSize,
      gs: this.locale.NUMBER_FORMATS.GROUP_SEP,
      ds: this.locale.NUMBER_FORMATS.DECIMAL_SEP,
      regex: this.locale.NUMBER_FORMATS.number_regex
    };
  }

  /**
   * Generate a localized time string from a javascript date object.
   * @param time - javascript date object
   * @returns {string}
   * compact variable is to diplay time more compact 11:00 AM becomes 11am -- 11:30 PM becomes 11:30pm
   * compact is overwritten if show24Hours is true.
   */
  localizeTime(time: Time | Date | undefined, padWithZero = true, compact = false): string | undefined {
    if (time == null) {
      return;
    }

    let separator = ':';
    let ampmNames = this.locale.DATETIME_FORMATS.AMPMS;
    let show24Hours = this.locale.DATETIME_FORMATS.shortTime === 'HH:mm';
    let hours: number = time.getHours();
    let mins: number = time.getMinutes();
    let hoursString: string;
    let minsString: string;

    if (show24Hours) {
      padWithZero = true;
      compact = false;
    } else {
      hours = ((hours + 11) % 12) + 1;
    }

    if (padWithZero && hours < 10 && !compact) {
      hoursString = '0' + hours;
    } else {
      hoursString = String(hours);
    }

    if (mins < 10) {
      minsString = '0' + mins;
    } else {
      minsString = String(mins);
    }

    if (compact) {
      if (mins === 0) {
        return hoursString + ampmNames[time.getHours() < 12 ? 0 : 1].toLowerCase();
      } else {
        return hoursString + separator + minsString + ampmNames[time.getHours() < 12 ? 0 : 1].toLowerCase();
      }
    } else {
      return hoursString + separator + minsString + (show24Hours ? '' : ' ' + ampmNames[time.getHours() < 12 ? 0 : 1]);
    }

  }

  deLocalizeTime(time: string): Time | undefined {
    if (time == null) {
      return;
    }

    let hours: number;
    let minutes: number;

    let timeTokens: {length: number} | null;
    if (this.locale.DATETIME_FORMATS.shortTime.endsWith('a')) {
      //AM PM time
      timeTokens = AM_PM_TIME_REGEX.exec(time);
      if (timeTokens == null || timeTokens.length < 4) {
        return;
      }

      hours = Number(timeTokens[1]);
      minutes = Number(timeTokens[2]);
      if (timeTokens[3].toLowerCase() === 'pm') {
        if (hours < 12) {
          hours += 12;
        }
      } else {
        if (hours === 12) {
          hours = 0;
        }
      }
    } else {
      //24 hour time
      return this.parse24HourTime(time);
    }

    return new SimpleTime(hours, minutes);
  }

  parse24HourTime(time: string): Time | undefined {
    let timeTokens = time.split(':');
    if (timeTokens.length !== 2) {
      return;
    }

    let hours = Number(timeTokens[0]);
    let minutes = Number(timeTokens[1]);

    if (isNaN(hours) || isNaN(minutes)) {
      return;
    }
    return new SimpleTime(hours, minutes);
  }

  isValidTime(localizedTime: string): boolean {
    let show24Hours = this.locale.DATETIME_FORMATS.shortTime === 'HH:mm';
    if (show24Hours) {
      return TWENTY_FOUR_HOUR_TIME_REGEX.test(localizedTime);
    } else {
      return AM_PM_TIME_REGEX.test(localizedTime);
    }
  }

  /**
   * Localize a number, and optionally round it to a specific number of decimal places
   * @param number, number to localize
   * @param decimalPlaces (optional), number of decimal places to round to
   * @param strictDecimals (optional), appends zeroes if number has fewer decimal places
   * @returns the localized version of the number
   */
  localizeNumber(number: number, decimalPlaces?: number, strictDecimals?: boolean): string | undefined {
    if (number == null || isNaN(number)) {
      return;
    }

    if (decimalPlaces != null && !isNaN(decimalPlaces)) {
      // round the number to n decimal places before localizing
      let multiplier = 1;
      for (let i = 0; i < decimalPlaces; i++) {
        multiplier *= 10;
      }
      number = Math.round(number * multiplier) / multiplier;
    }

    let formattedNumber = this.formatNumber(this.getNumericFormat(), number);

    if (strictDecimals === true && decimalPlaces > 0) {
      //Add zeroes and an appropriate decimal separator as padding to the localized number
      let numDecimals = 0;
      if (String(formattedNumber).includes(this.locale.NUMBER_FORMATS.DECIMAL_SEP)) {
        let parts = String(formattedNumber).split(this.locale.NUMBER_FORMATS.DECIMAL_SEP);
        numDecimals = parts[parts.length - 1].length;
      }
      if (numDecimals === 0) {
        formattedNumber = formattedNumber + this.locale.NUMBER_FORMATS.DECIMAL_SEP;
      }
      formattedNumber = formattedNumber + ('0').repeat(decimalPlaces - numDecimals);
    }

    return formattedNumber;
  }

  deLocalizeNumber(number: string): number | undefined {
    return this.removeNumberFormat(this.getNumericFormat(), number);
  }

  deLocalizeNumberToString(number: string): string | undefined {
    let result = this.removeNumberFormat(this.getNumericFormat(), number);
    if (result == null) {
      return;
    }
    return String(result);
  }

  removeNumberFormat(format: any, number: any): number | undefined {
    if (number == null) {
      return;
    }

    // IE does not like instantiating RegExp objects with empty values, so we have to
    // check for valid values in the grouping separator before using it in a RegExp.
    let gsNotBlank = (format.gs === '.' || format.gs === ',' || format.gs === ' ');
    number = number.toString();
    // We also want to check that only valid characters are present.
    let regexString = '^' + format.regex + '$';
    let validNumber = new RegExp(regexString, 'g');
    if (number !== '' && number.match(validNumber) != null) {
      if (gsNotBlank) {
        let gs = new RegExp('\\' + format.gs, 'g');
        number = number.replace(gs, '');
      }
      let ds = new RegExp('\\' + format.ds, 'g'); //The decimal separator is not expected to be empty
      number = parseFloat(number.replace(ds, '.'));
    } else {
      return;
    }

    if (!isNaN(number)) {
      return number;
    }
  }

  isValidNumber(numValue: string): boolean {
    if (numValue !== '') {
      let regexString = '^' + this.locale.NUMBER_FORMATS.number_regex + '$';
      let regex = new RegExp(regexString, 'g');
      if (regex.test(numValue)) {
        return (this.deLocalizeNumber(numValue) != null);
      } else {
        return false;
      }
    }
    return true;
  }

  /**
   * Adds a new field for an object with the value localized.
   * This function will add a new field to the obejct with the localized value.
   * @param object An object with a number value that needs to be localized
   * @param key The key of the value that needs to be localized.
   */
  localizeNumericObject(object: Object, key: string): void {
    object['_i18n_' + key] = this.localizeNumber(object[key]);
  }

  /**
   * This is the sister function of localizeNumericObject. If the localized value
   * can be parsed, it will update the original value with the deLocalized version.
   * @param object An object with a number value that has been localized using localizeNumericObject
   * @param key The key of the value that got localized.
   */
  deLocalizeNumericObject(object: Object, key: string): void {
    if (!object.hasOwnProperty('_i18n_' + key)) {
      return;
    }

    if (object['_i18n_' + key] == null || object['_i18n_' + key] === '') {
      object[key] = '';
      return;
    }

    object[key] = this.deLocalizeNumber(object['_i18n_' + key]);
  }

  /**
   * @deprecated Use localizeTime() instead.
   */
  makeStrFromTime(time: Date, padWithZero = true): string {
    return this.localizeTime.apply(this, arguments);
  }

  //TODO: Add unit tests
  /**
   * Creates a new Date object with the same day, month, and year of the passed in Date combined with the hours and minutes
   * of the time object.
   * @param date
   * @param time
   * @returns {Date} - A brand new date object.
   */
  combineDateAndTime(date: Date, time: Time): Date | undefined {
    if (date == null) {
      return;
    }

    if (time == null) {
      time = new SimpleTime(0, 0);
    }

    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes());
  }

  //TODO: Add unit tests
  /**
   * Creates a time object based on the hours and minutes in the date object.
   * @param date
   * @returns Time
   */
  extractTime(date: Date): Time {
    return new SimpleTime(date.getHours(), date.getMinutes());
  }

  /**
   * This is the function to format a time for display.
   */
  formatTime(time: Time, formatType: 'TIME' | 'TIME_PAD_ZEROS' | 'TIME_COMPACT'): string | undefined {
    if (time == null || formatType == null) {
      return '';
    }

    switch (formatType) {
      default: 
      case 'TIME':
        return this.localizeTime(time, false);
      case 'TIME_PAD_ZEROS':
        return this.localizeTime(time, true);
      case 'TIME_COMPACT':
        return this.localizeTime(time, false, true);
    }
  }

  /**
   * This is the function to format a date for display.
   */
  formatDate(date: Date, formatType: 'DATE' | 'DATE_PRETTY' | 'DATE_SR' | 'DATE_TIME' | 'DATE_TIME_PRETTY' | 'DATE_TIME_SR'): string {
    if (date == null) {
      return '';
    }

    switch (formatType) {
      case 'DATE_TIME':
        return this.getText('cu|date utils|xx at xx', {
          theDate: this.localizeDate(date),
          theTime: this.localizeTime(date)
        });
      case 'DATE_TIME_SR':
        return this.getText('cu|date utils|xx at xx', {
          theDate: this.localizeDatePretty(date, false) ,
          theTime: this.localizeTime(date, false)
        });
      case 'DATE_TIME_PRETTY':
        return this.getText('cu|date utils|xx at xx', {
          theDate: this.localizeDatePretty(date) ,
          theTime: this.localizeTime(date, false)
        });
      case 'DATE':
        return this.localizeDate(date);
      case 'DATE_SR':
        return this.localizeDatePretty(date, false);
      case 'DATE_PRETTY':
      default:
        return this.localizeDatePretty(date);
    }
  }

  private formatNumber(format, number: number): string | undefined {
    let ds = format.ds; // decimal separator
    let result = '';

    //curiously, isNaN(null) returns false.
    if (isNaN(number) || number == null) {
      if (number == null) {
        return;
      }

      return String(number);
    } else {
      result = number.toString();
      result = result.replace('.', ds);
      return result;
    }
  }

  private padZeros(number: number, padding: number): string | undefined {
    let paddedString = '';
    if (isNaN(number) || isNaN(padding) || number == null || padding == null) {
      if (number == null) {
        return;
      }

      return String(number);
    } else {
      paddedString = number.toString();
      while (paddedString.length < padding) {
        paddedString = '0' + paddedString;
      }

      return paddedString;
    }
  }
}

export let i18n = new I18nUtil();
